import { unifiedConfigService } from './UnifiedConfigurationService';
import { lambdaDataService } from './LambdaDataService';
import { logger } from '@/utils/logger';

// AWS Lambda Data API URL for currency operations - using environment variable
const AWS_DATA_API_URL = import.meta.env.VITE_AWS_DATA_API_URL || 'https://api.iwishbag.com/data';

export interface Currency {
  code: string;
  name: string;
  symbol: string;
  decimal_places?: number;
  is_active?: boolean;
  min_payment_amount?: number;
  thousand_separator?: string;
  decimal_separator?: string;
}

export interface CountrySettings {
  code: string;
  name: string;
  currency: string;
  rate_from_usd?: number;
  minimum_payment_amount?: number;
  decimal_places?: number;
  thousand_separator?: string;
  decimal_separator?: string;
  symbol_position?: string;
  symbol_space?: boolean;
}

class CurrencyService {
  private static instance: CurrencyService;
  private logThrottle = new Map<string, number>();
  
  // Multi-tier caching system
  private memoryCache: Map<string, { data: any; expires: number }> = new Map();
  private currencyCache: Map<string, Currency> = new Map();
  private allCurrenciesCache: Currency[] | null = null;
  private countryCurrencyMapCache: Map<string, string> = new Map();
  private cacheTimestamp: number = 0;
  
  // Cache durations
  private readonly MEMORY_TTL = 5 * 60 * 1000; // 5 minutes (fastest access)
  private readonly STORAGE_TTL = 30 * 60 * 1000; // 30 minutes (localStorage)
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes (legacy compatibility)

  private constructor() {
    // Clean up expired localStorage entries on startup
    this.cleanupExpiredStorage();
    // Initialize cache warmup
    this.scheduleEssentialDataPreload();
  }

  static getInstance(): CurrencyService {
    if (!CurrencyService.instance) {
      CurrencyService.instance = new CurrencyService();
    }
    return CurrencyService.instance;
  }

  /**
   * Check if cache is still valid
   */
  private isCacheValid(): boolean {
    return Date.now() - this.cacheTimestamp < this.CACHE_DURATION;
  }

  /**
   * Clear all caches
   */
  clearCache(): void {
    this.currencyCache.clear();
    this.allCurrenciesCache = null;
    this.countryCurrencyMapCache.clear();
    this.memoryCache.clear();
    this.cacheTimestamp = 0;
    
    // Clear localStorage cache
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('iwishbag_currency_')) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        logger.warn(`Failed to remove ${key} from localStorage:`, error);
      }
    });
    
    // CurrencyService log removed
  }

  /**
   * Unified 3-tier caching system: Memory → AWS Lambda → localStorage → Database
   */
  private async getCachedData<T>(
    key: string,
    fetcher: () => Promise<T>,
    ttl: number = this.STORAGE_TTL,
    d1Endpoint?: string
  ): Promise<T> {
    // Tier 1: Memory cache (instant access)
    const memoryCached = this.memoryCache.get(key);
    if (memoryCached && Date.now() < memoryCached.expires) {
      const timeLeft = memoryCached.expires - Date.now();
      // Throttle cache hit logs to prevent spam
      const now = Date.now();
      const lastLog = this.logThrottle.get(key) || 0;
      if (now - lastLog > 60000) { // Log at most every 60 seconds
        // CurrencyService log removed
        this.logThrottle.set(key, now);
      }
      return memoryCached.data;
    }

    // Tier 2: AWS Lambda Data API cache (global, fast)
    if (d1Endpoint && d1Endpoint.includes('/api/countries')) {
      try {
        const fetchStart = performance.now();
        const response = await fetch(`${AWS_DATA_API_URL}/countries`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'User-Agent': 'iwishbag-frontend/1.0'
          }
        });
        const fetchTime = performance.now() - fetchStart;

        if (response.ok) {
          const data = await response.json();
          // Check if AWS API returned valid data
          if (Array.isArray(data) && data.length > 0) {
            // Cache in memory
            this.memoryCache.set(key, {
              data,
              expires: Date.now() + this.MEMORY_TTL
            });

            // Also cache in localStorage
            this.cacheToLocalStorage(key, data, ttl);

            return data;
          }
        } else {
          logger?.warn('AWS Data API error, falling back to database', { status: response.status, key });
        }
      } catch (error) {
        logger?.warn('AWS Data API cache error, falling back', { error, key });
      }
    }

    // Tier 3: localStorage cache (very fast)
    try {
      const storageKey = `iwishbag_currency_${key}`;
      const storedData = localStorage.getItem(storageKey);
      
      if (storedData) {
        const parsed = JSON.parse(storedData);
        const timeLeft = parsed.expires - Date.now();
        if (Date.now() < parsed.expires) {
          // Promote to memory cache
          this.memoryCache.set(key, {
            data: parsed.data,
            expires: Date.now() + this.MEMORY_TTL
          });
          
          return parsed.data;
        }
      }
    } catch (error) {
      logger.warn(`[CurrencyService] Storage cache error for ${key}:`, error);
    }

    // Tier 4: Database/API call
    const fetchStart = performance.now();
    const data = await fetcher();
    const fetchTime = performance.now() - fetchStart;
    
    // Cache in both tiers
    this.memoryCache.set(key, {
      data,
      expires: Date.now() + this.MEMORY_TTL
    });

    this.cacheToLocalStorage(key, data, ttl);
    return data;
  }

  /**
   * Cache data to localStorage with error handling
   */
  private cacheToLocalStorage(key: string, data: any, ttl: number): void {
    try {
      const storageKey = `iwishbag_currency_${key}`;
      localStorage.setItem(storageKey, JSON.stringify({
        data,
        expires: Date.now() + ttl,
        cached_at: Date.now(),
        source: 'database'
      }));
    } catch (error) {
      logger.warn(`[CurrencyService] Failed to cache in localStorage:`, error);
    }
  }

  /**
   * Clean up expired localStorage entries
   */
  private cleanupExpiredStorage(): void {
    const now = Date.now();
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('iwishbag_currency_')) {
        try {
          const data = JSON.parse(localStorage.getItem(key) || '{}');
          if (data.expires && now > data.expires) {
            keysToRemove.push(key);
          }
        } catch (error) {
          // Invalid data, remove it
          keysToRemove.push(key);
        }
      }
    }
    
    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        logger.warn(`Failed to cleanup ${key}:`, error);
      }
    });
    
    if (keysToRemove.length > 0) {
      // CurrencyService log removed
    }
  }

  /**
   * Get all unique currencies from country_settings table with enhanced caching
   */
  async getAllCurrencies(): Promise<Currency[]> {
    // Temporarily clear cache in development to ensure fresh data
    if (import.meta.env.DEV) {
      // CurrencyService log removed
      this.clearCache();
    }
    
    return this.getCachedData(
      'all_currencies',
      async () => {
        try {
          // CurrencyService log removed

          // Query Lambda Data API instead of Supabase directly
          const countriesResponse = await lambdaDataService.getCountries();

          if (!countriesResponse.success || !countriesResponse.data || countriesResponse.data.length === 0) {
            logger.warn('No countries found from Lambda Data API, using fallback currencies');
            return this.getFallbackCurrencies();
          }

          // CurrencyService log removed

          // Group by currency and get the most complete data for each
          const currencyMap = new Map<string, Currency>();

          countriesResponse.data.forEach((country) => {
            if (!country.currency) return;

            const existing = currencyMap.get(country.currency);
            const currency: Currency = {
              code: country.currency,
              name: this.getCurrencyNameSync(country.currency),
              symbol: this.getCurrencySymbolSync(country.currency),
              decimal_places: this.getCurrencyDecimalPlacesSync(country.currency),
              min_payment_amount: country.min_shipping ?? undefined,
              thousand_separator: ',',
              decimal_separator: '.',
              is_active: true,
            };

            // Keep the one with more complete data (prefer non-null values)
            if (!existing || (country.min_shipping && !existing.min_payment_amount)) {
              currencyMap.set(country.currency, currency);
            }
          });

          const currencies = Array.from(currencyMap.values());
          // CurrencyService log removed

          // Update legacy caches for backward compatibility
          this.allCurrenciesCache = currencies;
          this.cacheTimestamp = Date.now();

          // Update individual currency cache
          currencies.forEach((currency) => {
            this.currencyCache.set(currency.code, currency);
          });

          return currencies;
        } catch (error) {
          logger.error('Error in getAllCurrencies:', error);
          return this.getFallbackCurrencies();
        }
      },
      24 * 60 * 60 * 1000 // 24 hours - currencies don't change often
      // '/api/countries' // AWS endpoint for currency data
    ).then(async result => {
      // Handle AWS response format
      if (result && 'countries' in result) {
        // Convert country data to Currency format
        return result.countries.map(country => ({
          code: country.currency,
          name: country.currency,
          symbol: country.symbol,
          decimal_places: this.getCurrencyDecimalPlacesSync(country.currency),
          is_active: true,
        }));
      }
      return result;
    });
  }

  /**
   * Get currency by code
   */
  async getCurrency(code: string): Promise<Currency | null> {
    if (this.currencyCache.has(code) && this.isCacheValid()) {
      return this.currencyCache.get(code) || null;
    }

    // If not in cache, fetch all currencies (which will populate cache)
    const allCurrencies = await this.getAllCurrencies();
    return allCurrencies.find((c) => c.code === code) || null;
  }

  /**
   * Get country-to-currency mapping from unified configuration system
   */
  async getCountryCurrencyMap(): Promise<Map<string, string>> {
    if (this.countryCurrencyMapCache.size > 0 && this.isCacheValid()) {
      return this.countryCurrencyMapCache;
    }

    try {
      // Get all countries from unified configuration
      const allCountries = await unifiedConfigService.getAllCountries();

      if (!allCountries || Object.keys(allCountries).length === 0) {
        logger.warn('No countries found in unified config, using fallback mapping');
        return this.getFallbackCountryCurrencyMap();
      }

      const map = new Map<string, string>();
      Object.entries(allCountries).forEach(([countryCode, countryConfig]) => {
        if (countryCode && countryConfig.currency) {
          map.set(countryCode, countryConfig.currency);
        }
      });

      this.countryCurrencyMapCache = map;
      this.cacheTimestamp = Date.now();

      return map;
    } catch (error) {
      logger.error('Error in getCountryCurrencyMap:', error);
      return this.getFallbackCountryCurrencyMap();
    }
  }

  /**
   * Get currency for a specific country with enhanced caching
   */
  async getCurrencyForCountry(countryCode: string): Promise<string> {
    const cacheKey = `country_currency_${countryCode}`;
    
    return this.getCachedData(
      cacheKey,
      async () => {
        const map = await this.getCountryCurrencyMap();
        return map.get(countryCode) || 'USD';
      },
      24 * 60 * 60 * 1000, // 24 hours
      `/api/countries/${countryCode}` // AWS endpoint
    ).then(result => {
      // Handle AWS response format
      if (result && typeof result === 'object' && 'country' in result && result.country) {
        return result.country.currency || 'USD';
      }
      // Handle direct country data from AWS
      if (Array.isArray(result)) {
        const country = result.find(c => c.code === countryCode);
        return country?.currency || 'USD';
      }
      return result;
    });
  }

  /**
   * Get currency for a specific country (synchronous version using cached data)
   */
  getCurrencyForCountrySync(countryCode: string): string {
    // Use cached data if available
    if (this.countryCurrencyMapCache.has(countryCode) && this.isCacheValid()) {
      return this.countryCurrencyMapCache.get(countryCode) || 'USD';
    }

    // Fall back to hardcoded mapping
    const fallbackMap = this.getFallbackCountryCurrencyMap();
    return fallbackMap.get(countryCode) || 'USD';
  }

  /**
   * Get full country settings including currency and exchange rate data
   * This is the function needed for proper currency conversion
   */
  async getCountrySettings(countryCode: string): Promise<CountrySettings | null> {
    console.log(`[CURRENCY SERVICE] Getting country settings for: ${countryCode}`);

    try {
      const data = await lambdaDataService.getCountrySettings(countryCode);

      if (!data) {
        logger.warn(`[CURRENCY SERVICE] No country settings found for ${countryCode}`);
        return null;
      }

      console.log(`[CURRENCY SERVICE] ✅ Found settings for ${countryCode}:`, {
        currency: data.currency,
        rate_from_usd: data.rate_from_usd,
        name: data.name
      });

      return {
        code: data.code,
        name: data.name,
        currency: data.currency,
        rate_from_usd: data.rate_from_usd,
        minimum_payment_amount: data.min_shipping,
        decimal_places: 2, // Default for now
        thousand_separator: ',',
        decimal_separator: '.',
        symbol_position: 'before',
        symbol_space: false,
      };
    } catch (error) {
      logger.error(`[CURRENCY SERVICE] Exception getting country settings for ${countryCode}:`, error);
      return null;
    }
  }

  /**
   * Get all currencies available for a specific country
   * (For now, returns all currencies but highlights the country's default)
   */
  async getAvailableCurrenciesForCountry(countryCode: string): Promise<{
    default: string;
    all: Currency[];
  }> {
    const allCurrencies = await this.getAllCurrencies();
    const defaultCurrency = await this.getCurrencyForCountry(countryCode);

    return {
      default: defaultCurrency,
      all: allCurrencies,
    };
  }

  /**
   * Get currency name (synchronous) - public for backward compatibility
   */
  getCurrencyName(currencyCode: string): string {
    return this.getCurrencyNameSync(currencyCode);
  }

  /**
   * Get currency symbol (synchronous) - public for formatting functions
   */
  getCurrencySymbol(currencyCode: string): string {
    return this.getCurrencySymbolSync(currencyCode);
  }

  /**
   * Get currency symbol - with database fallback to hardcoded
   */
  private getCurrencySymbolSync(currencyCode: string): string {
    // Hardcoded symbols as fallback
    const symbols: Record<string, string> = {
      USD: '$',
      EUR: '€',
      GBP: '£',
      INR: '₹',
      NPR: 'NRs',
      CAD: 'C$',
      AUD: 'A$',
      JPY: '¥',
      CNY: '¥',
      SGD: 'S$',
      AED: 'د.إ',
      SAR: 'ر.س',
      EGP: 'ج.م',
      TRY: '₺',
      IDR: 'Rp',
      MYR: 'RM',
      PHP: '₱',
      THB: '฿',
      VND: '₫',
      KRW: '₩',
    };
    return symbols[currencyCode] || currencyCode;
  }

  /**
   * Get currency name - with hardcoded fallback
   */
  private getCurrencyNameSync(currencyCode: string): string {
    const names: Record<string, string> = {
      USD: 'US Dollar',
      EUR: 'Euro',
      GBP: 'British Pound',
      INR: 'Indian Rupee',
      NPR: 'Nepalese Rupee',
      CAD: 'Canadian Dollar',
      AUD: 'Australian Dollar',
      JPY: 'Japanese Yen',
      CNY: 'Chinese Yuan',
      SGD: 'Singapore Dollar',
      AED: 'UAE Dirham',
      SAR: 'Saudi Riyal',
      EGP: 'Egyptian Pound',
      TRY: 'Turkish Lira',
      IDR: 'Indonesian Rupiah',
      MYR: 'Malaysian Ringgit',
      PHP: 'Philippine Peso',
      THB: 'Thai Baht',
      VND: 'Vietnamese Dong',
      KRW: 'South Korean Won',
    };
    return names[currencyCode] || currencyCode;
  }

  /**
   * Get currency decimal places
   */
  /**
   * Get decimal places for currency from unified configuration or fallback
   */
  async getCurrencyDecimalPlaces(currencyCode: string): Promise<number> {
    try {
      // Try to find a country that uses this currency
      const allCountries = await unifiedConfigService.getAllCountries();

      for (const [countryCode, countryConfig] of Object.entries(allCountries)) {
        if (countryConfig.currency === currencyCode) {
          // Use the same fallback logic for consistency
          return this.getCurrencyDecimalPlacesSync(currencyCode);
        }
      }
    } catch (error) {
      logger.error('Error fetching decimal places from unified config:', error);
    }

    return this.getCurrencyDecimalPlacesSync(currencyCode);
  }

  /**
   * Synchronous fallback for decimal places
   */
  private getCurrencyDecimalPlacesSync(currencyCode: string): number {
    // Some currencies don't use decimal places
    const noDecimalCurrencies = ['JPY', 'KRW', 'VND', 'IDR'];
    return noDecimalCurrencies.includes(currencyCode) ? 0 : 2;
  }

  /**
   * Fallback currencies when database is unavailable
   */
  private getFallbackCurrencies(): Currency[] {
    const fallbackCurrencyCodes = [
      'USD',
      'EUR',
      'GBP',
      'INR',
      'NPR',
      'CAD',
      'AUD',
      'JPY',
      'CNY',
      'SGD',
      'AED',
      'SAR',
      'IDR',
      'MYR',
      'PHP',
      'THB',
      'VND',
    ];

    return fallbackCurrencyCodes.map((code) => ({
      code,
      name: this.getCurrencyNameSync(code),
      symbol: this.getCurrencySymbolSync(code),
      decimal_places: this.getCurrencyDecimalPlacesSync(code),
      is_active: true,
    }));
  }

  /**
   * Fallback country-currency mapping when database is unavailable
   */
  private getFallbackCountryCurrencyMap(): Map<string, string> {
    const fallbackMap = new Map([
      ['US', 'USD'],
      ['IN', 'INR'],
      ['NP', 'NPR'],
      ['CA', 'CAD'],
      ['AU', 'AUD'],
      ['GB', 'GBP'],
      ['JP', 'JPY'],
      ['CN', 'CNY'],
      ['SG', 'SGD'],
      ['AE', 'AED'],
      ['SA', 'SAR'],
      ['ID', 'IDR'],
      ['MY', 'MYR'],
      ['PH', 'PHP'],
      ['TH', 'THB'],
      ['VN', 'VND'],
      ['KR', 'KRW'],
    ]);
    return fallbackMap;
  }

  /**
   * Get fallback country-currency mapping (public access)
   */
  getFallbackCountryCurrencyMapSync(): Map<string, string> {
    return this.getFallbackCountryCurrencyMap();
  }

  /**
   * Get country code for a given currency (reverse lookup)
   */
  async getCountryForCurrency(currencyCode: string): Promise<string | null> {
    // Debug logging for NPR currency lookup
    if (currencyCode === 'NPR') {
      
    }
    
    try {
      // Get all countries from unified configuration
      const allCountries = await unifiedConfigService.getAllCountries();

      if (currencyCode === 'NPR') {
        
      }

      // Find the first country that uses this currency
      for (const [countryCode, countryConfig] of Object.entries(allCountries)) {
        if (countryConfig.currency === currencyCode) {
          if (currencyCode === 'NPR') {
            
          }
          return countryCode;
        }
      }
    } catch (error) {
      if (currencyCode === 'NPR') {
        console.error(`[US-NPR-DEBUG] Error in unified config lookup:`, error);
      }
      logger.error('Error fetching country for currency from unified config:', error);
    }

    // Fallback to hardcoded mapping
    const fallbackResult = this.getCountryForCurrencySync(currencyCode);
    if (currencyCode === 'NPR') {
      
    }
    return fallbackResult;
  }

  /**
   * Get country code for a given currency (sync fallback)
   */
  getCountryForCurrencySync(currencyCode: string): string | null {
    const fallbackMap = this.getFallbackCountryCurrencyMap();
    
    if (currencyCode === 'NPR') {
      
      
      let found = false;
      for (const [country, currency] of fallbackMap.entries()) {
        if (currency === 'NPR') {
          
          found = true;
        }
      }
      if (!found) {
        
      }
    }
    
    for (const [country, currency] of fallbackMap.entries()) {
      if (currency === currencyCode) {
        if (currencyCode === 'NPR') {
          
        }
        return country;
      }
    }
    return null;
  }

  /**
   * Check if a currency code is valid
   */
  async isValidCurrency(currencyCode: string): Promise<boolean> {
    const allCurrencies = await this.getAllCurrencies();
    return allCurrencies.some((c) => c.code === currencyCode);
  }

  /**
   * Get currency information for display purposes
   */
  async getCurrencyDisplayInfo(currencyCode: string): Promise<{
    code: string;
    name: string;
    symbol: string;
    formatted: string;
  }> {
    const currency = await this.getCurrency(currencyCode);

    return {
      code: currencyCode,
      name: currency?.name || this.getCurrencyNameSync(currencyCode),
      symbol: currency?.symbol || this.getCurrencySymbolSync(currencyCode),
      formatted: `${currency?.name || this.getCurrencyNameSync(currencyCode)} (${currencyCode})`,
    };
  }

  /**
   * Get minimum payment amount for a currency
   */
  async getMinimumPaymentAmount(currencyCode: string): Promise<number> {
    try {
      // Get all countries from unified configuration
      const allCountries = await unifiedConfigService.getAllCountries();

      // Find a country that uses this currency and has minimum payment amount
      for (const [countryCode, countryConfig] of Object.entries(allCountries)) {
        if (countryConfig.currency === currencyCode && countryConfig.minimum_payment_amount) {
          return countryConfig.minimum_payment_amount;
        }
      }
    } catch (error) {
      logger.error('Error fetching minimum payment amount from unified config:', error);
    }

    // Fallback to hardcoded values
    return this.getMinimumPaymentAmountSync(currencyCode);
  }

  /**
   * Synchronous fallback for minimum payment amount
   */
  getMinimumPaymentAmountSync(currencyCode: string): number {
    const minimumAmounts: Record<string, number> = {
      USD: 10,
      EUR: 10,
      GBP: 8,
      INR: 750,
      NPR: 1200,
      CAD: 15,
      AUD: 15,
      JPY: 1100,
      CNY: 70,
      SGD: 15,
      AED: 40,
      SAR: 40,
      EGP: 200,
      TRY: 100,
      IDR: 150000,
      MYR: 45,
      PHP: 550,
      THB: 350,
      VND: 240000,
      KRW: 12000,
    };
    return minimumAmounts[currencyCode] || 10;
  }

  /**
   * Get currency value category based on minimum payment amount (purchasing power indicator)
   */
  private getCurrencyValueCategory(currencyCode: string): 'ultra_low' | 'low' | 'medium' | 'high' {
    const minPayment = this.getMinimumPaymentAmountSync(currencyCode);
    
    // Ultra-low value currencies (high minimum payments indicate low value per unit)
    if (minPayment >= 1000) return 'ultra_low';  // KRW ₩6000, NPR ₨665, INR ₹415, etc.
    
    // Low value currencies  
    if (minPayment >= 100) return 'low';         // RUB ₽450, THB ฿150, etc.
    
    // Medium value currencies
    if (minPayment >= 20) return 'medium';       // Many standard currencies
    
    // High value currencies (low minimum payments indicate high value per unit)
    return 'high';                               // USD $5-10, EUR €5-10, GBP £5-8
  }

  /**
   * Get smart rounding rules based on currency value category and amount size
   */
  private getSmartRoundingRules(currencyCode: string, amount: number): {
    decimalPlaces: number;
    roundToNearest: number;
  } {
    const category = this.getCurrencyValueCategory(currencyCode);
    const dbDecimalPlaces = this.getCurrencyDecimalPlacesSync(currencyCode);
    
    // For zero-decimal currencies, keep database configuration
    if (dbDecimalPlaces === 0) {
      return {
        decimalPlaces: 0,
        roundToNearest: amount >= 10000 ? 100 : (amount >= 1000 ? 10 : 1)
      };
    }

    // PRECISION FIXED: Always maintain 2 decimal places to prevent rounding issues
    // The old "smart rounding" was causing ₹399 → NPR conversion problems
    return { decimalPlaces: 2, roundToNearest: 1 };
  }

  /**
   * Get currency formatting options with database integration and smart rounding
   */
  getCurrencyFormatOptions(currencyCode: string, amount?: number): {
    decimalPlaces: number;
    thousandSeparator: string;
    decimalSeparator: string;
    roundToNearest?: number;
  } {
    // Get smart rounding rules if amount is provided
    const smartRules = amount !== undefined 
      ? this.getSmartRoundingRules(currencyCode, amount)
      : { decimalPlaces: this.getCurrencyDecimalPlacesSync(currencyCode), roundToNearest: 1 };

    // Default formatting with smart decimal places
    const options = {
      decimalPlaces: smartRules.decimalPlaces,
      thousandSeparator: ',',
      decimalSeparator: '.',
      roundToNearest: smartRules.roundToNearest,
    };

    // Currency-specific separator overrides
    if (['EUR'].includes(currencyCode)) {
      options.thousandSeparator = '.';
      options.decimalSeparator = ',';
    }

    return options;
  }

  /**
   * @deprecated ⚠️ DO NOT USE for user-facing displays! Causes rounding issues.
   * Use formatAmountWithFinancialPrecision() from quoteCurrencyUtils.ts instead.
   * 
   * This function uses "smart rounding" which truncates precision and causes
   * currency conversion display problems (e.g., ₹399 NPR rounding issues).
   * 
   * Format amount according to currency rules with smart rounding
   */
  formatAmount(amount: number, currencyCode: string): string {
    const currency = this.getCurrencySymbolSync(currencyCode);
    
    // Handle null, undefined, or NaN values
    const safeAmount = amount == null || isNaN(amount) ? 0 : amount;
    
    // Get smart formatting options based on amount
    const options = this.getCurrencyFormatOptions(currencyCode, safeAmount);

    // Use amount as-is, let locale formatter handle precision
    const finalRounded = safeAmount;

    // Format with separators
    const parts = finalRounded.toFixed(options.decimalPlaces).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, options.thousandSeparator);

    const formatted = parts.join(options.decimalSeparator);
    return `${currency}${formatted}`;
  }

  /**
   * Check if amount meets minimum payment requirement
   */
  async isValidPaymentAmount(amount: number, currencyCode: string): Promise<boolean> {
    const minimum = await this.getMinimumPaymentAmount(currencyCode);
    return amount >= minimum;
  }

  /**
   * Check if amount meets minimum payment requirement (sync fallback)
   */
  isValidPaymentAmountSync(amount: number, currencyCode: string): boolean {
    const minimum = this.getMinimumPaymentAmountSync(currencyCode);
    return amount >= minimum;
  }

  /**
   * Get all supported payment gateway currencies
   */
  getPaymentGatewayCurrencies(): string[] {
    // These are currencies typically supported by payment gateways
    return ['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD', 'SGD', 'AED', 'SAR'];
  }

  /**
   * Check if currency is supported by payment gateways
   */
  isSupportedByPaymentGateway(currencyCode: string): boolean {
    return this.getPaymentGatewayCurrencies().includes(currencyCode);
  }

  /**
   * Get exchange rate with enhanced 3-tier caching: Memory → AWS Lambda → localStorage → Database
   * Uses 2-tier system: Shipping Routes → Country Settings → Error
   *
   * @param originCountry - Origin country code (e.g. 'US', 'IN')
   * @param destinationCountry - Destination country code (e.g. 'IN', 'NP')
   * @returns Promise<number> - Exchange rate or throws error
   */
  async getExchangeRate(originCountry: string, destinationCountry: string): Promise<number> {
    const cacheKey = `rate_${originCountry}_${destinationCountry}`;
    
    // Debug logging for NP -> US conversion, GBP conversions, IN -> NP conversion, and IN -> CA conversion
    const isNPUSConversion = originCountry === 'NP' && destinationCountry === 'US';
    const isGBPConversion = originCountry === 'GB' || destinationCountry === 'GB';
    const isINNPConversion = originCountry === 'IN' && destinationCountry === 'NP';
    const isINCAConversion = originCountry === 'IN' && destinationCountry === 'CA';
    
    if (isNPUSConversion) {
      
    }
    if (isGBPConversion) {
      
    }
    if (isINNPConversion) {
      
    }
    if (isINCAConversion) {
      
      
      
    }
    
    // Same currency = 1.0 (no caching needed)
    if (originCountry === destinationCountry) {
      if (isINCAConversion) {
        
      }
      return 1.0;
    }

    // CRITICAL: For exchange rates, check shipping_tiers FIRST before any caching
    // This ensures business-defined rates take priority over calculated cross-rates
    
    try {
      // Direct shipping_tiers check (no caching) - highest priority
      // Look for country-specific tiers with direct exchange rates
      
      const shippingResponse = await lambdaDataService.getShippingTiers({
        scope: `${originCountry}->${destinationCountry}`,
        tier_type: 'country'
      });

      const shippingTier = shippingResponse.success ?
        shippingResponse.data.find(tier => tier.direct_exchange_rate !== null && tier.active) : null;
      const routeError = !shippingResponse.success;

      if (isNPUSConversion) {
        
      }
      if (isINNPConversion) {
        
      }
      if (isINCAConversion) {
        
        
      }

      if (!routeError && shippingTier?.direct_exchange_rate) {
        if (isNPUSConversion) {
          
        }
        if (isINNPConversion) {
          
        }
        if (isINCAConversion) {
          
          
        }
        return shippingTier.direct_exchange_rate;
      } else if (routeError) {
        if (isINCAConversion) {
          
        }
        
        // Enhanced debugging for all errors
        
        // Handle errors and skip to unified config calculation
        if (routeError.code === 'PGRST106' || routeError.message?.includes('406') || routeError.status === 406) {
          // Skip shipping_tiers and go directly to unified config calculation
        }
      } else {
        if (isINCAConversion) {
          
        }
      }
    } catch (priorityError) {
      
      if (isINCAConversion) {
        
      }
      
      // Enhanced debugging for catch block errors
      
      // Handle 406 errors from JavaScript fetch (browser)
      if (priorityError.message?.includes('406') || (priorityError as any)?.status === 406) {
      }
    }

    // Use AWS Lambda countries endpoint for currency data
    const awsEndpoint = `/api/countries`;
    
    // Use enhanced caching for exchange rates with proper error handling for stale data
    try {
      const result = await this.getCachedData(
        cacheKey,
        async () => {
        
        try {
          // Tier 1: Fallback to unified configuration USD-based conversion  
          
          if (isINCAConversion) {
            
          }
          
          const [originConfig, destConfig] = await Promise.all([
            unifiedConfigService.getCountryConfig(originCountry),
            unifiedConfigService.getCountryConfig(destinationCountry),
          ]);
          
          if (isINCAConversion) {
            
          }

          if (isNPUSConversion) {
            // NPUS conversion logic handled by main flow
            if (originConfig && destConfig) {
              // Configuration validated
            }
          }
          
          if (isINCAConversion) {
            // INCA conversion logic handled by main flow
            if (originConfig && destConfig) {
              // Configuration validated
            }
          }
          
          if (isGBPConversion) {
            // GBP conversion logic handled by main flow
            if (originConfig && destConfig) {
              // Configuration validated for GBP conversion
            }
          }

          if (!originConfig || !destConfig) {
            
            // Fallback to default exchange rate (1.0 for same currency, or common rates)
            if (originCountry === destinationCountry) {
              return 1.0;
            }
            // Common fallback rates for missing configurations
            // Note: GB rates removed since they exist in country_settings database
            const fallbackRates: Record<string, number> = {
              DE_NP: 134.5, // EUR to NPR approximate
              NP_DE: 0.0074, // NPR to EUR approximate
              IN_NP: 1.6, // INR to NPR approximate
              NP_IN: 0.625, // NPR to INR approximate
              US_NP: 134.5, // USD to NPR approximate
              NP_US: 0.0074, // NPR to USD approximate
              IN_US: 0.012, // INR to USD (83.33 INR per USD)
              US_IN: 83.33, // USD to INR
              IN_CA: 0.016, // INR to CAD approximate (1 INR = 0.016 CAD)
              CA_IN: 62.5, // CAD to INR approximate (1 CAD = 62.5 INR)
            };
            const fallbackKey = `${originCountry}_${destinationCountry}`;
            const fallbackRate = fallbackRates[fallbackKey] || 1.0;
            if (isNPUSConversion) {
              
            }
            if (isGBPConversion) {
              
            }
            if (isINCAConversion) {
              
              
            }
            return fallbackRate;
          }

          const originRate = originConfig.rate_from_usd;
          const destRate = destConfig.rate_from_usd;

          if (!originRate || !destRate || originRate <= 0 || destRate <= 0) {
            throw new Error(
              `Invalid exchange rates: ${originCountry}=${originRate}, ${destinationCountry}=${destRate}`,
            );
          }

          // Calculate cross rate via USD: destination_rate / origin_rate
          const crossRate = destRate / originRate;
          
          if (isGBPConversion) {
            
          }
          
          if (isINCAConversion) {
            
            
            
            
          }
          
          // 🚨 CHECK DATABASE DATA FRESHNESS AND ENHANCE WITH LIVE RATES IF NEEDED
          const dbOriginUpdated = originConfig.updated_at ? new Date(originConfig.updated_at).getTime() : 0;
          const dbDestUpdated = destConfig.updated_at ? new Date(destConfig.updated_at).getTime() : 0;
          const dbOriginAge = Date.now() - dbOriginUpdated;
          const dbDestAge = Date.now() - dbDestUpdated;
          const dbMaxStaleAge = 24 * 60 * 60 * 1000; // 24 hours for database tolerance
          const dbIsStale = Math.max(dbOriginAge, dbDestAge) > dbMaxStaleAge;
          
          const dbOriginAgeHours = Math.round(dbOriginAge / (1000 * 60 * 60));
          const dbDestAgeHours = Math.round(dbDestAge / (1000 * 60 * 60));
          
          if (dbIsStale) {
            
            // Try to get live rate from external API as final fallback
            try {
              const liveRate = await this.getLiveExchangeRate(originCountry, destinationCountry);
              if (liveRate !== null) {
                return liveRate;
              }
            } catch (externalError) {
            }
          }

          // Compare with live rates for transparency (async)
          this.logExternalRateComparison(originCountry, destinationCountry, crossRate).catch(console.error);

          if (isNPUSConversion) {
            
          }
          return crossRate;
        } catch (error) {
          if (isINCAConversion) {
            
          }
          throw new Error(`Exchange rate unavailable for ${originCountry} to ${destinationCountry}: ${error.message}`);
        }
      },
      6 * 60 * 60 * 1000, // 6 hours - exchange rates are relatively stable
      awsEndpoint
    );
    
    if (isNPUSConversion) {
      
    }
    
    // Handle AWS countries response format
    if (Array.isArray(result)) {
      const countries = result;
      if (isNPUSConversion) {
        
      }
        
        // Find origin and destination countries in the AWS response
        const originCountryData = countries.find(c => c.code === originCountry);
        const destCountryData = countries.find(c => c.code === destinationCountry);
        
        if (isNPUSConversion) {
          // NPUS conversion validation
          if (originCountryData && destCountryData) {
            // Country data validated for NPUS conversion - proceeding with standard flow
          }
        }

        // Add detailed freshness analysis
        if (originCountryData?.updated_at) {
          const originTimestamp = new Date(originCountryData.updated_at * 1000);
          const originAge = Date.now() - originTimestamp.getTime();
          const originAgeHours = Math.round(originAge / (1000 * 60 * 60));
        }
        
        if (destCountryData?.updated_at) {
          const destTimestamp = new Date(destCountryData.updated_at * 1000);
          const destAge = Date.now() - destTimestamp.getTime();
          const destAgeHours = Math.round(destAge / (1000 * 60 * 60));
        }
        
        // Use rate_from_usd from AWS Lambda data
        if (originCountryData?.rate_from_usd && destCountryData?.rate_from_usd) {
          // Calculate cross-rate: dest_rate / origin_rate
          const crossRate = destCountryData.rate_from_usd / originCountryData.rate_from_usd;

          if (isNPUSConversion) {
            // NPUS conversion detected - using cross-rate calculation
          }
          
          // 🚨 DATA FRESHNESS VALIDATION - Check AWS data freshness
          const currentTime = Date.now();
          const originTimestamp = originCountryData.updated_at ? new Date(originCountryData.updated_at).getTime() : 0;
          const destTimestamp = destCountryData.updated_at ? new Date(destCountryData.updated_at).getTime() : 0;
          const originAge = currentTime - originTimestamp;
          const destAge = currentTime - destTimestamp;
          // More lenient for critical currency pairs (USD, INR, NPR)
          const isCriticalPair = (originCountry === 'NP' && destinationCountry === 'US') || 
                                (originCountry === 'US' && destinationCountry === 'NP') ||
                                (originCountry === 'IN' && destinationCountry === 'US') ||
                                (originCountry === 'US' && destinationCountry === 'IN');
          
          const maxAcceptableAge = isCriticalPair 
            ? 7 * 24 * 60 * 60 * 1000 // 7 days for critical pairs
            : 48 * 60 * 60 * 1000; // 48 hours for others
            
          const isStale = Math.max(originAge, destAge) > maxAcceptableAge;
          
          const originAgeHours = Math.round(originAge / (1000 * 60 * 60));
          const destAgeHours = Math.round(destAge / (1000 * 60 * 60));
          
          if (isStale) {
            if (isNPUSConversion) {
              // NPUS stale data detected - forcing database fallback
            }
            if (isGBPConversion) {
              // GBP stale data detected - forcing database fallback
            }
            
            // Log comparison with external API for transparency
            this.logExternalRateComparison(originCountry, destinationCountry, crossRate).catch(console.error);
            
            // AWS data is stale, use emergency fallback rates
            logger.warn(`[CurrencyService] AWS data is stale (Origin: ${originAgeHours}h, Dest: ${destAgeHours}h), using emergency rates`);

            // Use emergency fallback rates instead of throwing error
            const emergencyRates: Record<string, number> = {
              'IN_CA': 0.016, 'CA_IN': 62.5, 'IN_NP': 1.6, 'NP_IN': 0.625,
              'US_NP': 134.5, 'NP_US': 0.0074, 'IN_US': 0.012, 'US_IN': 83.33
            };
            const emergencyKey = `${originCountry}_${destinationCountry}`;
            const emergencyRate = emergencyRates[emergencyKey];
            if (emergencyRate) {
              logger.info(`[CurrencyService] Using emergency rate for ${emergencyKey}: ${emergencyRate}`);
              return emergencyRate;
            }
            throw new Error('AWS_STALE_DATA');
            
          } else {
            if (isNPUSConversion) {
              // NPUS fresh data - proceeding with AWS cross-rate
            }
            
            // Still compare with external API for quality monitoring
            if (originAgeHours > 6 || destAgeHours > 6) {
              this.logExternalRateComparison(originCountry, destinationCountry, crossRate).catch(console.error);
            }
            
            // Return fresh AWS data
            if (isNPUSConversion) {
              // NPUS returning fresh cross-rate from AWS
            }
            return crossRate;
          }
        } else {
          if (isNPUSConversion) {
            // NPUS missing exchange rate data - falling back to database
          }
        }
      }
      
      // Handle direct rate response format
      if (typeof result === 'object' && result && 'rate' in result && result.rate !== null) {
        return result.rate;
      }
      
      if (isNPUSConversion) {
        
      }
      
      // If result is not a number, this is problematic - we need to extract or calculate the rate
      if (typeof result !== 'number') {
        if (isNPUSConversion) {
          console.error(`[US-NPR-DEBUG] ERROR: Result is not a number! Type: ${typeof result}, Value:`, result);
        }
        throw new Error(`Exchange rate calculation failed - got ${typeof result} instead of number`);
      }
      
      return result;
    } catch (error) {
      
      if (isINCAConversion) {
        
        
        
      }
      
      // If this is an AWS stale data error, use emergency fallback rates
      if (error.message?.includes('AWS_STALE_DATA')) {
        if (isINCAConversion) {
          
        }
        
        // Emergency fallback for known currency pairs when all else fails
        const emergencyRates: Record<string, number> = {
          'IN_CA': 0.016, // INR to CAD approximate (1 INR = 0.016 CAD)
          'CA_IN': 62.5,  // CAD to INR approximate (1 CAD = 62.5 INR)
          'IN_NP': 1.6,   // INR to NPR approximate
          'NP_IN': 0.625, // NPR to INR approximate
          'US_NP': 134.5, // USD to NPR approximate
          'NP_US': 0.0074, // NPR to USD approximate
          'IN_US': 0.012, // INR to USD (83.33 INR per USD)
          'US_IN': 83.33, // USD to INR
        };
        
        const emergencyKey = `${originCountry}_${destinationCountry}`;
        const emergencyRate = emergencyRates[emergencyKey];
        
        if (emergencyRate) {
          if (isINCAConversion) {
            
            
          }
          return emergencyRate;
        }
      }
      
      throw error;
    }
  }

  /**
   * Get live exchange rate from external APIs as final fallback
   * @private
   */
  private async getLiveExchangeRate(originCountry: string, destinationCountry: string): Promise<number | null> {
    try {
      console.log(`🌐 [LiveRate] Fetching live exchange rate: ${originCountry} → ${destinationCountry}`);
      
      // Get currency codes
      const originCurrency = this.getCurrencyForCountrySync(originCountry);
      const destCurrency = this.getCurrencyForCountrySync(destinationCountry);
      
      console.log(`🌐 [LiveRate] Currency mapping: ${originCountry}(${originCurrency}) → ${destinationCountry}(${destCurrency})`);
      
      // Try multiple external APIs in order of preference
      const externalSources = [
        {
          name: 'ExchangeRate-API',
          url: `https://api.exchangerate-api.com/v4/latest/${originCurrency}`,
          parseResponse: (data: any) => data.rates?.[destCurrency]
        },
        {
          name: 'Fixer.io (Free)',
          url: `https://api.fixer.io/latest?base=${originCurrency}&symbols=${destCurrency}`,
          parseResponse: (data: any) => data.rates?.[destCurrency]
        }
      ];
      
      for (const source of externalSources) {
        try {
          console.log(`🌐 [LiveRate] Trying ${source.name}...`);
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
          
          const response = await fetch(source.url, { 
            signal: controller.signal,
            headers: { 
              'User-Agent': 'iwishbag-exchange-service/1.0',
              'Accept': 'application/json'
            }
          });
          
          clearTimeout(timeoutId);
          
          if (!response.ok) {
            console.log(`❌ [LiveRate] ${source.name} HTTP error: ${response.status}`);
            continue;
          }
          
          const data = await response.json();
          const rate = source.parseResponse(data);
          
          if (rate && typeof rate === 'number' && rate > 0) {
            console.log(`✅ [LiveRate] ${source.name} SUCCESS: ${originCurrency} → ${destCurrency} = ${rate}`);
            
            // Apply country-specific adjustments if this is India or Nepal
            let adjustedRate = rate;
            if (destinationCountry === 'IN') {
              adjustedRate = rate + 3; // Add 3 for India (same as server function)
              console.log(`🔧 [LiveRate] Applied India adjustment: ${rate} + 3 = ${adjustedRate}`);
            } else if (destinationCountry === 'NP') {
              adjustedRate = rate + 2; // Add 2 for Nepal (same as server function)
              console.log(`🔧 [LiveRate] Applied Nepal adjustment: ${rate} + 2 = ${adjustedRate}`);
            }
            
            return adjustedRate;
          } else {
            console.log(`❌ [LiveRate] ${source.name} invalid rate:`, rate);
          }
        } catch (error) {
          console.log(`❌ [LiveRate] ${source.name} error:`, error.message);
        }
      }
      
      console.log(`❌ [LiveRate] All external APIs failed for ${originCountry} → ${destinationCountry}`);
      return null;
      
    } catch (error) {
      console.log(`💥 [LiveRate] Critical error in live rate fetch:`, error.message);
      return null;
    }
  }

  /**
   * Compare AWS rate with external API to verify freshness
   * @private
   */
  private async logExternalRateComparison(originCountry: string, destinationCountry: string, awsRate: number): Promise<void> {
    try {
      
      // Get currency codes
      const originCurrency = this.getCurrencyForCountrySync(originCountry);
      const destCurrency = this.getCurrencyForCountrySync(destinationCountry);
      
      
      // Try multiple external APIs for comparison
      const externalSources = [
        {
          name: 'ExchangeRate-API',
          url: `https://api.exchangerate-api.com/v4/latest/${originCurrency}`
        },
        {
          name: 'Fixer.io',
          url: `https://api.fixer.io/latest?base=${originCurrency}&symbols=${destCurrency}`
        }
      ];
      
      let liveRate: number | null = null;
      let usedSource = '';
      
      for (const source of externalSources) {
        try {
          console.log(`🌐 [RateValidation] Trying ${source.name}...`);
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 3000);
          
          const response = await fetch(source.url, { 
            signal: controller.signal,
            headers: { 'User-Agent': 'iwishbag-rate-validator/1.0' }
          });
          
          clearTimeout(timeoutId);
          
          if (!response.ok) {
            console.log(`❌ [RateValidation] ${source.name} failed: ${response.status}`);
            continue;
          }
          
          const data = await response.json();
          
          if (source.name === 'ExchangeRate-API' && data.rates?.[destCurrency]) {
            liveRate = data.rates[destCurrency];
            usedSource = source.name;
            console.log(`✅ [RateValidation] ${source.name} live rate: ${liveRate}`);
            break;
          } else if (source.name === 'Fixer.io' && data.rates?.[destCurrency]) {
            liveRate = data.rates[destCurrency];
            usedSource = source.name;
            console.log(`✅ [RateValidation] ${source.name} live rate: ${liveRate}`);
            break;
          }
        } catch (error) {
          console.log(`❌ [RateValidation] ${source.name} error:`, error.message);
        }
      }
      
      if (liveRate !== null) {
        const difference = Math.abs(awsRate - liveRate);
        const percentageDiff = ((difference / liveRate) * 100);


        console.log(`📊 [RateValidation] AWS Lambda API: ${awsRate.toFixed(4)}`);
        console.log(`📊 [RateValidation] Live ${usedSource}: ${liveRate.toFixed(4)}`);
        console.log(`📊 [RateValidation] Difference:      ${difference.toFixed(4)} (${percentageDiff.toFixed(2)}%)`);

        if (percentageDiff > 2) {
          console.log(`🚨 [RateValidation] SIGNIFICANT DISCREPANCY: AWS rate is ${percentageDiff.toFixed(2)}% off from live rate!`);

        } else if (percentageDiff > 0.5) {
          console.log(`⚠️ [RateValidation] MINOR DISCREPANCY: AWS rate is ${percentageDiff.toFixed(2)}% off from live rate`);
        } else {
          console.log(`✅ [RateValidation] RATES MATCH: AWS and live rates are very close (${percentageDiff.toFixed(2)}% diff)`);
        }
      } else {
        console.log(`❌ [RateValidation] Could not fetch live rate for comparison from external APIs`);
      }
      
    } catch (error) {
      console.log(`💥 [RateValidation] External rate comparison failed:`, error.message);
    }
  }

  /**
   * Get exchange rate for currency conversion (simplified interface) with enhanced caching
   * @param fromCurrency - Source currency code (e.g. 'USD', 'INR')
   * @param toCurrency - Target currency code (e.g. 'INR', 'NPR')
   * @returns Promise<number> - Exchange rate or throws error
   */
  async getExchangeRateByCurrency(fromCurrency: string, toCurrency: string): Promise<number> {
    if (fromCurrency === toCurrency) {
      return 1.0;
    }

    const cacheKey = `currency_${fromCurrency}_${toCurrency}`;
    
    // Debug logging for US->NPR conversion and INR->NPR conversion
    const isUSNPRConversion = (fromCurrency === 'USD' && toCurrency === 'NPR') || (fromCurrency === 'NPR' && toCurrency === 'USD');
    const isINRNPRConversion = (fromCurrency === 'INR' && toCurrency === 'NPR') || (fromCurrency === 'NPR' && toCurrency === 'INR');
    if (isUSNPRConversion) {
      
    }
    if (isINRNPRConversion) {
      
    }
    
    return this.getCachedData(
      cacheKey,
      async () => {
        try {
          // Get countries for these currencies
          const fromCountry = await this.getCountryForCurrency(fromCurrency);
          const toCountry = await this.getCountryForCurrency(toCurrency);
          
          if (isUSNPRConversion) {
            
          }
          if (isINRNPRConversion) {
            
          }

          if (!fromCountry || !toCountry) {
            if (isUSNPRConversion) {
              console.error(`[US-NPR-DEBUG] Country mapping FAILED: ${fromCurrency} -> ${fromCountry}, ${toCurrency} -> ${toCountry}`);
            }
            if (isINRNPRConversion) {
              console.error(`[INR-NPR-DEBUG] Country mapping FAILED: ${fromCurrency} -> ${fromCountry}, ${toCurrency} -> ${toCountry}`);
            }
            throw new Error(`Cannot find countries for currencies: ${fromCurrency}, ${toCurrency}`);
          }

          const rate = await this.getExchangeRate(fromCountry, toCountry);
          if (isUSNPRConversion) {
            
          }
          if (isINRNPRConversion) {
            
          }
          
          return rate;
        } catch (error) {
          if (isUSNPRConversion) {
            console.error(`[US-NPR-DEBUG] Currency exchange rate ERROR:`, error);
          }
          
          // Check if this is AWS stale data error - use emergency rates
          const errorMessage = error instanceof Error ? error.message : String(error);
          if (errorMessage.includes('AWS_STALE_DATA')) {
            console.log(`[AWS_STALE_DATA] Using emergency rates for ${fromCurrency}→${toCurrency}`);

            const emergencyRates: Record<string, number> = {
              'USD_NPR': 134.5, 'NPR_USD': 0.0074, 'INR_NPR': 1.6, 'NPR_INR': 0.625,
              'USD_INR': 83.33, 'INR_USD': 0.012, 'INR_CAD': 0.016, 'CAD_INR': 62.5
            };

            const emergencyKey = `${fromCurrency}_${toCurrency}`;
            const emergencyRate = emergencyRates[emergencyKey];

            if (emergencyRate) {
              console.log(`[AWS_STALE_DATA] ✅ Emergency rate found: ${fromCurrency}→${toCurrency} = ${emergencyRate}`);
              return emergencyRate;
            } else {
              console.log(`[AWS_STALE_DATA] ⚠️ No emergency rate available for ${fromCurrency}→${toCurrency}`);
            }
          }
          
          logger.error(
            `[CurrencyService] Currency exchange rate failed ${fromCurrency}→${toCurrency}:`,
            error,
          );
          throw new Error(`Exchange rate unavailable for ${fromCurrency} to ${toCurrency}`);
        }
      },
      6 * 60 * 60 * 1000 // 6 hours
    );
  }

  /**
   * Convert amount between currencies
   * @param amount - Amount to convert
   * @param fromCurrency - Source currency code
   * @param toCurrency - Target currency code
   * @returns Promise<number> - Converted amount
   */
  async convertAmount(amount: number, fromCurrency: string, toCurrency: string): Promise<number> {
    if (fromCurrency === toCurrency) {
      return amount;
    }

    const rate = await this.getExchangeRateByCurrency(fromCurrency, toCurrency);
    return amount * rate;
  }

  /**
   * Convert amount using direct currency rates (bypasses country mapping)
   * Use this for cart/checkout to avoid "object instead of number" database errors
   * Same approach as admin quote calculator and quote page
   * 
   * @param amount - Amount to convert
   * @param fromCurrency - Source currency code (INR, USD, NPR, etc.)
   * @param toCurrency - Target currency code (INR, USD, NPR, etc.)
   * @returns Promise<number> - Converted amount
   */
  async convertAmountDirect(amount: number, fromCurrency: string, toCurrency: string): Promise<number> {
    if (fromCurrency === toCurrency) {
      return amount;
    }

    console.log(`[DIRECT CONVERSION] Converting ${amount} ${fromCurrency} → ${toCurrency}`);

    try {
      // Try direct conversion using AWS Lambda Data API first
      const countriesResponse = await lambdaDataService.getCountries();

      if (countriesResponse.success && countriesResponse.data) {
        const fromCountryData = countriesResponse.data.find(c => c.currency === fromCurrency);
        const toCountryData = countriesResponse.data.find(c => c.currency === toCurrency);

        if (fromCountryData?.rate_from_usd && toCountryData?.rate_from_usd) {
          // Cross-rate calculation: dest_rate / origin_rate
          const directRate = toCountryData.rate_from_usd / fromCountryData.rate_from_usd;
          const convertedAmount = amount * directRate;

          console.log(`[DIRECT CONVERSION] ✅ AWS Lambda conversion: ${amount} ${fromCurrency} * ${directRate} = ${convertedAmount} ${toCurrency}`);
          return convertedAmount;
        }
      }

      console.log(`[DIRECT CONVERSION] ⚠️ AWS Lambda lookup failed, falling back to external APIs`);
      
      // Fallback to external API (same as admin quote calculator approach)
      // This should work for critical pairs like NPR-INR, USD-NPR, etc.
      const externalRate = await this.getExternalExchangeRate(fromCurrency, toCurrency);
      if (externalRate) {
        const convertedAmount = amount * externalRate;
        console.log(`[DIRECT CONVERSION] ✅ External API conversion: ${amount} ${fromCurrency} * ${externalRate} = ${convertedAmount} ${toCurrency}`);
        return convertedAmount;
      }

      // Last resort: try the original method but with better error handling
      console.log(`[DIRECT CONVERSION] ⚠️ External API failed, trying original method with error handling`);
      const rate = await this.getExchangeRateByCurrency(fromCurrency, toCurrency);
      return amount * rate;

    } catch (error) {
      console.error(`[DIRECT CONVERSION] ❌ All conversion methods failed:`, error);
      logger.error('Direct currency conversion failed:', { amount, fromCurrency, toCurrency, error });
      
      // Graceful degradation: return original amount
      logger.warn(`Falling back to original amount (no conversion) due to exchange rate failure`);
      return amount;
    }
  }

  /**
   * Get exchange rate from external APIs (for direct conversion fallback)
   * @private
   */
  private async getExternalExchangeRate(fromCurrency: string, toCurrency: string): Promise<number | null> {
    try {
      // Use the same external API approach as admin quote calculator
      const response = await fetch(`https://api.exchangerate-api.com/v4/latest/${fromCurrency}`);
      const data = await response.json();
      
      if (data && data.rates && data.rates[toCurrency]) {
        return data.rates[toCurrency];
      }
      
      return null;
    } catch (error) {
      console.warn('External exchange rate API failed:', error);
      return null;
    }
  }

  /**
   * Get currency by country code
   * @param countryCode - Country code (e.g. 'US', 'IN')
   * @returns Currency code (e.g. 'USD', 'INR')
   */
  getCurrencyByCountry(countryCode: string): string {
    return this.getCurrencyForCountrySync(countryCode);
  }

  // ============================================================================
  // PERFORMANCE ENHANCEMENT METHODS
  // ============================================================================

  /**
   * Schedule essential data preload for faster app startup
   */
  private scheduleEssentialDataPreload(): void {
    // Preload after a short delay to not block initial app rendering
    setTimeout(() => {
      this.preloadEssentials().catch(error => {
        logger.warn('[CurrencyService] Essential data preload failed:', error);
      });
    }, 2000);
  }

  /**
   * Preload essential currency data for instant app startup
   */
  async preloadEssentials(): Promise<void> {
    const essentials = [
      () => this.getAllCurrencies(),
      () => this.getCurrencyForCountry('US'),
      () => this.getCurrencyForCountry('IN'), 
      () => this.getCurrencyForCountry('NP'),
      () => this.getExchangeRate('US', 'IN'),
      () => this.getExchangeRate('US', 'NP')
    ];

    await Promise.allSettled(essentials.map(fn => fn()));
    // CurrencyService log removed
  }

  /**
   * Warm up cache with common currency pairs
   */
  async warmUpCache(): Promise<void> {
    // CurrencyService log removed
    
    const commonPairs = [
      // USD pairs (most common)
      ['US', 'IN'], ['US', 'NP'], ['US', 'GB'], ['US', 'AU'], ['US', 'CA'],
      // Regional pairs
      ['IN', 'NP'], ['DE', 'GB'], ['AU', 'NZ'], ['CA', 'US'],
      // Reverse pairs
      ['IN', 'US'], ['NP', 'US'], ['GB', 'US'], ['AU', 'US'], ['CA', 'US']
    ];

    // Load all pairs concurrently but with small delay to avoid overwhelming
    for (let i = 0; i < commonPairs.length; i++) {
      const [origin, dest] = commonPairs[i];
      try {
        await this.getExchangeRate(origin, dest);
        if (i < commonPairs.length - 1) {
          // Small delay to avoid overwhelming the API
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      } catch (error) {
        logger.warn(`[CurrencyService] Failed to warm ${origin}→${dest}:`, error);
      }
    }

    // CurrencyService log removed
  }

  /**
   * Get cache statistics for monitoring
   */
  getCacheStats(): {
    memoryCacheSize: number;
    storageCacheSize: number;
    hitRate: string;
  } {
    let storageCacheSize = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('iwishbag_currency_')) {
        storageCacheSize++;
      }
    }

    return {
      memoryCacheSize: this.memoryCache.size,
      storageCacheSize,
      hitRate: 'Available after testing'
    };
  }

  /**
   * Batch update exchange rates (for scheduled updates)
   */
  async batchUpdateExchangeRates(rates: Record<string, number>): Promise<void> {
    // CurrencyService log removed
    
    Object.entries(rates).forEach(([pair, rate]) => {
      const cacheKey = `rate_${pair}`;
      
      // Update memory cache
      this.memoryCache.set(cacheKey, {
        data: rate,
        expires: Date.now() + this.MEMORY_TTL
      });
      
      // Update localStorage cache
      this.cacheToLocalStorage(cacheKey, rate, 6 * 60 * 60 * 1000);
    });

    // CurrencyService log removed
  }

  /**
   * Format a group of amounts with proportional rounding to ensure they sum to the target total
   * This solves the cumulative rounding error problem where individual rounded components
   * don't sum to the rounded total.
   * 
   * @param components - Array of {label, amount} objects to format
   * @param targetTotal - The precise total that components should sum to
   * @param currencyCode - Currency for formatting
   * @returns Object with formatted components and total
   */
  formatAmountGroup(
    components: Array<{ label: string; amount: number }>,
    targetTotal: number,
    currencyCode: string
  ): {
    components: Array<{ label: string; amount: number; formatted: string }>;
    total: { amount: number; formatted: string };
    adjustments: Array<{ label: string; adjustment: number }>;
  } {
    // Proportional rounding debug logging removed

    // Step 1: Get the desired display total (rounded)
    const roundedTotal = this.getRoundedAmount(targetTotal, currencyCode);
    // CurrencyService log removed

    // Step 2: Calculate each component's share of the total
    const totalPrecise = components.reduce((sum, component) => sum + component.amount, 0);
    const componentShares = components.map(component => ({
      ...component,
      share: totalPrecise > 0 ? component.amount / totalPrecise : 0
    }));

    // Step 3: Allocate the rounded total proportionally
    const allocatedComponents = componentShares.map(component => ({
      ...component,
      allocatedAmount: roundedTotal * component.share
    }));

    // Step 4: Round individual components using standard rounding
    const roundedComponents = allocatedComponents.map(component => ({
      ...component,
      roundedAmount: this.getRoundedAmount(component.allocatedAmount, currencyCode)
    }));

    // Step 5: Calculate rounding error and distribute it
    const sumOfRounded = roundedComponents.reduce((sum, c) => sum + c.roundedAmount, 0);
    const roundingError = roundedTotal - sumOfRounded;
    // CurrencyService log removed

    // Step 6: Distribute the error proportionally to largest components
    const finalComponents = [...roundedComponents];
    if (Math.abs(roundingError) > 0.001) {
      // Sort by allocated amount (largest first) to distribute error fairly
      const sortedIndices = finalComponents
        .map((c, index) => ({ index, allocatedAmount: c.allocatedAmount }))
        .sort((a, b) => b.allocatedAmount - a.allocatedAmount)
        .map(item => item.index);

      // Get the smallest currency unit for this currency
      const minUnit = this.getMinimumCurrencyUnit(currencyCode);
      let remainingError = roundingError;

      // Distribute the error across components, starting with the largest ones
      let componentIndex = 0;
      while (Math.abs(remainingError) >= minUnit / 2 && componentIndex < sortedIndices.length) {
        const index = sortedIndices[componentIndex];
        
        // Calculate how much error this component should absorb
        // For large errors, distribute more to larger components
        let adjustmentSteps = Math.floor(Math.abs(remainingError) / minUnit);
        
        // Don't adjust more than reasonable for any single component
        const maxStepsForComponent = Math.max(1, Math.floor(adjustmentSteps / (sortedIndices.length - componentIndex)));
        adjustmentSteps = Math.min(adjustmentSteps, maxStepsForComponent);
        
        const adjustment = (remainingError > 0 ? minUnit : -minUnit) * adjustmentSteps;
        finalComponents[index].roundedAmount += adjustment;
        remainingError -= adjustment;

        // CurrencyService log removed
        
        componentIndex++;
      }
    }

    // Step 7: Format all amounts and track adjustments
    const result = {
      components: finalComponents.map((component, index) => ({
        label: component.label,
        amount: component.roundedAmount,
        formatted: this.formatExactAmount(component.roundedAmount, currencyCode)
      })),
      total: {
        amount: roundedTotal,
        formatted: this.formatExactAmount(roundedTotal, currencyCode)
      },
      adjustments: finalComponents.map((component, index) => ({
        label: component.label,
        adjustment: component.roundedAmount - components[index].amount
      })).filter(adj => Math.abs(adj.adjustment) > 0.001)
    };

    // Verification: Ensure components sum to total
    const verificationSum = result.components.reduce((sum, c) => sum + c.amount, 0);
    if (Math.abs(verificationSum - roundedTotal) > 0.001) {
      // CurrencyService log removed
    } else {
      // CurrencyService log removed
    }

    return result;
  }

  /**
   * Get the rounded amount using the currency's smart rounding rules
   * @private
   */
  private getRoundedAmount(amount: number, currencyCode: string): number {
    const options = this.getCurrencyFormatOptions(currencyCode, amount);
    
    // Use amount as-is to avoid aggressive rounding
    return amount;
  }

  /**
   * Get the minimum currency unit (smallest denomination)
   * @private
   */
  private getMinimumCurrencyUnit(currencyCode: string): number {
    const decimalPlaces = this.getCurrencyDecimalPlacesSync(currencyCode);
    return Math.pow(10, -decimalPlaces);
  }

  /**
   * Format an exact amount without additional rounding
   * @private
   */
  private formatExactAmount(amount: number, currencyCode: string): string {
    const currency = this.getCurrencySymbolSync(currencyCode);
    const options = this.getCurrencyFormatOptions(currencyCode);
    
    // Use the amount as-is, just format with separators
    const parts = amount.toFixed(options.decimalPlaces).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, options.thousandSeparator);
    
    const formatted = parts.join(options.decimalSeparator);
    return `${currency}${formatted}`;
  }
}

// Export singleton instance
export const currencyService = CurrencyService.getInstance();

// Export convenience functions for backward compatibility
export const getAllCurrencies = () => currencyService.getAllCurrencies();
export const getCurrencyForCountry = (countryCode: string) =>
  currencyService.getCurrencyForCountry(countryCode);
export const getCurrencyForCountrySync = (countryCode: string) =>
  currencyService.getCurrencyForCountrySync(countryCode);
export const getCountrySettings = (countryCode: string) =>
  currencyService.getCountrySettings(countryCode);
export const getAvailableCurrenciesForCountry = (countryCode: string) =>
  currencyService.getAvailableCurrenciesForCountry(countryCode);
export const isValidCurrency = (currencyCode: string) =>
  currencyService.isValidCurrency(currencyCode);
export const getCurrencyDisplayInfo = (currencyCode: string) =>
  currencyService.getCurrencyDisplayInfo(currencyCode);
/** @deprecated ⚠️ Use formatAmountWithFinancialPrecision() from quoteCurrencyUtils.ts instead */
export const formatAmount = (amount: number, currencyCode: string) => {
  // Import here to avoid circular dependency
  const { formatAmountWithFinancialPrecision } = require('@/utils/quoteCurrencyUtils');
  return formatAmountWithFinancialPrecision(amount, currencyCode);
};
export const formatAmountGroup = (
  components: Array<{ label: string; amount: number }>,
  targetTotal: number,
  currencyCode: string
) => {
  // Use the proper formatting method for financial precision
  const { formatAmountWithFinancialPrecision } = require('@/utils/quoteCurrencyUtils');
  return currencyService.formatAmountGroup(components, targetTotal, currencyCode, formatAmountWithFinancialPrecision);
};
export const convertAmount = (amount: number, fromCurrency: string, toCurrency: string) =>
  currencyService.convertAmount(amount, fromCurrency, toCurrency);
export const getCurrencyByCountry = (countryCode: string) =>
  currencyService.getCurrencyByCountry(countryCode);

// Export performance enhancement functions
export const warmUpCurrencyCache = () => currencyService.warmUpCache();
export const getCurrencyCacheStats = () => currencyService.getCacheStats();
export const clearCurrencyCache = () => currencyService.clearCache();
export const preloadEssentialCurrencyData = () => currencyService.preloadEssentials();
export const batchUpdateExchangeRates = (rates: Record<string, number>) => 
  currencyService.batchUpdateExchangeRates(rates);

// Performance test function (migrated from OptimizedCurrencyService)
export async function testCurrencyPerformance() {
  // CurrencyService log removed
  
  const testPairs = [
    ['US', 'IN'], ['US', 'NP'], ['IN', 'NP'], ['GB', 'US'], ['AU', 'US']
  ];
  const iterations = 3;
  
  // Test performance with caching
  console.log('Testing enhanced CurrencyService...');
  const times: number[] = [];
  
  for (const [origin, dest] of testPairs) {
    for (let i = 0; i < iterations; i++) {
      const start = performance.now();
      await currencyService.getExchangeRate(origin, dest);
      times.push(performance.now() - start);
    }
  }
  
  const avgTime = times.reduce((a, b) => a + b) / times.length;
  
  // CurrencyService log removed
  console.log(`Enhanced service: ${avgTime.toFixed(2)}ms avg`);
  console.log(`Cache stats:`, currencyService.getCacheStats());
  
  return {
    avgTime: avgTime.toFixed(2),
    cacheStats: currencyService.getCacheStats()
  };
}
